import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type ConditionalStyles, type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import type { ConfigurationPreview, ConfigurationPreviewKind, ConfigurationPreviewRow } from "../../api/configuration";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import adminStyles from "../../components/admin/Admin.module.css";
import { Dropdown, type DropdownOption } from "../../components/shared/Dropdown";
import { useColumnFilters, type ColumnFilterSpec } from "../../components/shared/columnFilters";
import panel from "../../components/admin/AdminPanel.module.css";
import styles from "./ConfigurationContent.module.css";

/** A preview row as the table shows it: its kind beside it, and an id that tells two kinds' rows apart. */
interface PreviewTableRow extends ConfigurationPreviewRow {
  id: string;
  kind: string;
}

/** Refused rows are the ones an administrator must fix before the import can apply, so they are highlighted. The status text still says so. */
const refusedRowStyles: ConditionalStyles<PreviewTableRow>[] = [
  {
    when: (row) => row.status === "Refused",
    style: {
      backgroundColor: "var(--color-danger-bg)",
      borderLeft: "4px solid var(--color-danger-text)",
    },
  },
];

/**
 * What applying a previewed file would do: the kill-switch state and any blocking reasons, the counts by kind, the rows filterable by
 * kind, and the warnings an administrator must see before applying. It is page content, not a toast, because it is the data being
 * reviewed. Focus moves here when a preview arrives. The rows are a sortable, filterable table, and refused rows are highlighted.
 */
export function ConfigurationPreviewView({ preview }: { preview: ConfigurationPreview }): React.JSX.Element {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const filterId = useId();
  const [kindFilter, setKindFilter] = useState<string>("all");

  useEffect(() => {
    headingRef.current?.focus();
  }, [preview]);

  const totals = useMemo(() => sumKinds(preview.kinds), [preview.kinds]);

  const kindOptions = useMemo<DropdownOption[]>(
    () => [{ value: "all", label: t("configAdmin.preview.allKinds") }, ...preview.kinds.map((kind) => ({ value: kind.kind, label: kind.kind }))],
    [preview.kinds, t],
  );

  const rows = useMemo<PreviewTableRow[]>(
    () =>
      preview.kinds
        .filter((kind) => kindFilter === "all" || kind.kind === kindFilter)
        .flatMap((kind) => kind.rows.map((row) => ({ ...row, kind: kind.kind, id: `${kind.kind}|${row.key}` }))),
    [preview.kinds, kindFilter],
  );

  // The columns the preview can be filtered on, each by what its cells show.
  const previewFilters = useMemo((): ColumnFilterSpec<PreviewTableRow>[] => [
    { id: "kind", kind: "set", value: (row) => row.kind },
    { id: "key", kind: "text", value: (row) => row.key },
    { id: "status", kind: "set", value: (row) => t(`configAdmin.preview.status.${row.status}`) },
    { id: "detail", kind: "text", value: (row) => row.detail ?? "" },
  ], [t]);
  const { filtered: shownRows, heading } = useColumnFilters(rows, previewFilters);

  const columns = useMemo<TableColumn<PreviewTableRow>[]>(
    () => [
      { id: "kind", name: heading("kind", t("configAdmin.preview.columns.kind")), selector: (row) => row.kind, sortable: true, width: "9rem", wrap: true },
      { id: "key", name: heading("key", t("configAdmin.preview.columns.key")), selector: (row) => row.key, sortable: true, grow: 2, wrap: true },
      {
        id: "status",
        name: heading("status", t("configAdmin.preview.columns.status")),
        selector: (row) => t(`configAdmin.preview.status.${row.status}`),
        sortable: true,
        width: "10rem",
        wrap: true,
      },
      { id: "detail", name: heading("detail", t("configAdmin.preview.columns.detail")), selector: (row) => row.detail ?? "", sortable: true, grow: 3, wrap: true },
      { id: "changes", name: t("configAdmin.preview.columns.changes"), selector: (row) => row.changes.join("; "), sortable: true, grow: 2, wrap: true },
    ],
    [t, heading],
  );

  return (
    <div className={`${panel.panel} ${styles.summary} ${styles.section}`} aria-labelledby={`${filterId}-heading`}>
      <h3 id={`${filterId}-heading`} ref={headingRef} tabIndex={-1}>
        {t("configAdmin.preview.heading")}
      </h3>
      <p>{t("configAdmin.preview.exported", { instance: preview.instanceName, exported: formatDate(preview.exportedAtUtc), expires: formatDate(preview.expiresAtUtc) })}</p>
      <p>{preview.killSwitchOn ? t("configAdmin.preview.killSwitchOn") : t("configAdmin.preview.killSwitchOff")}</p>
      <p>
        <strong>{preview.canApply ? t("configAdmin.preview.canApply") : t("configAdmin.preview.cannotApply")}</strong>
      </p>

      {preview.blockingReasons.length > 0 && (
        <section aria-labelledby={`${filterId}-blocking`}>
          <h4 id={`${filterId}-blocking`}>{t("configAdmin.preview.blockingHeading")}</h4>
          <ul>
            {preview.blockingReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </section>
      )}

      <p>{t("configAdmin.preview.counts", totals)}</p>
      <p className={styles.hint}>{t("configAdmin.preview.notInSourceNote")}</p>

      <div className={styles.field}>
        <label htmlFor={`${filterId}-kind`}>{t("configAdmin.preview.kindFilter")}</label>
        <Dropdown id={`${filterId}-kind`} options={kindOptions} value={kindFilter} onChange={setKindFilter} />
      </div>

      <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
        <DataTable
          persistTableHead
          columns={columns}
          data={shownRows}
          className={adminStyles.peopleDataTable}
          keyField="id"
          ariaLabel={t("configAdmin.preview.heading")}
          noDataComponent={t("configAdmin.preview.noRows")}
          theme={adminTableTheme}
          pagination
          paginationComponent={TablePager}
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100]}
          highlightOnHover
          columnSeparator="subtle"
          conditionalRowStyles={refusedRowStyles}
        />
      </div>

      <section aria-labelledby={`${filterId}-new`}>
        <h4 id={`${filterId}-new`}>{t("configAdmin.preview.newPeopleHeading")}</h4>
        {preview.newPeopleGettingAccess.length === 0 ? (
          <p>{t("configAdmin.preview.none")}</p>
        ) : (
          <ul>
            {preview.newPeopleGettingAccess.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby={`${filterId}-vendor`}>
        <h4 id={`${filterId}-vendor`}>{t("configAdmin.preview.vendorHeading")}</h4>
        {preview.vendorEffect.length === 0 ? (
          <p>{t("configAdmin.preview.none")}</p>
        ) : (
          <ul>
            {preview.vendorEffect.map((item) => (
              <li key={`${item.email}|${item.provider}`}>{t("configAdmin.preview.vendorItem", { email: item.email, provider: item.provider, change: item.change })}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function sumKinds(kinds: ConfigurationPreviewKind[]): { created: number; updated: number; unchanged: number; refused: number; notInSource: number } {
  return kinds.reduce(
    (total, kind) => ({
      created: total.created + kind.created,
      updated: total.updated + kind.updated,
      unchanged: total.unchanged + kind.unchanged,
      refused: total.refused + kind.refused,
      notInSource: total.notInSource + kind.notInSource,
    }),
    { created: 0, updated: 0, unchanged: 0, refused: 0, notInSource: 0 },
  );
}

function formatDate(value: string): string {
  return value.slice(0, 10);
}
